import { useRef, useState } from 'react'
import { ArrowDown, ArrowUp, ClipboardPaste, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import type { FieldOption } from '../../types'
import { addOption, bulkAddOptions, moveOption, optionValueFromLabel, removeOption, uniqueKey, updateOption } from './model'

interface Props {
    options: FieldOption[]
    onChange: (o: FieldOption[]) => void
}

/** Options for select fields. Values auto-follow the label only for options added in this session. */
export default function OptionsEditor({ options, onChange }: Props) {
    const auto = useRef(new Set<string>())
    const [bulk, setBulk] = useState('')
    const [bulkOpen, setBulkOpen] = useState(false)
    const focusLast = useRef(false)

    const counts = new Map<string, number>()
    options.forEach((o) => counts.set(o.value, (counts.get(o.value) ?? 0) + 1))

    const setLabel = (i: number, label: string) => {
        const o = options[i]
        if (auto.current.has(o.value)) {
            const others = options.filter((_, j) => j !== i).map((x) => x.value)
            const value = uniqueKey(optionValueFromLabel(label), others)
            auto.current.delete(o.value)
            auto.current.add(value)
            onChange(updateOption(options, i, { label, value }))
        } else {
            onChange(updateOption(options, i, { label }))
        }
    }
    const setValue = (i: number, value: string) => {
        auto.current.delete(options[i].value)
        onChange(updateOption(options, i, { value }))
    }
    const add = () => {
        const next = addOption(options)
        auto.current.add(next[next.length - 1].value)
        focusLast.current = true
        onChange(next)
    }
    const addBulk = () => {
        const next = bulkAddOptions(options, bulk)
        next.slice(options.length).forEach((o) => auto.current.add(o.value))
        onChange(next)
        setBulk('')
        setBulkOpen(false)
    }

    return (
        <FieldSet>
            <FieldLegend variant="label">Options</FieldLegend>
            <FieldDescription>The value is what gets stored and used in conditions; the label is what people see.</FieldDescription>
            <ul className="flex flex-col gap-2">
                {options.map((o, i) => {
                    const dup = (counts.get(o.value) ?? 0) > 1
                    const invalidValue = o.value === '' || dup
                    return (
                        <li key={i} className="flex items-center gap-1.5">
                            <Input
                                aria-label={`Option ${i + 1} label`}
                                value={o.label}
                                placeholder="Label"
                                className="min-w-0 flex-1"
                                ref={(el) => {
                                    if (el && focusLast.current && i === options.length - 1) {
                                        focusLast.current = false
                                        el.focus()
                                        el.select()
                                    }
                                }}
                                onChange={(e) => setLabel(i, e.target.value)}
                            />
                            <Input
                                aria-label={`Option ${i + 1} value`}
                                aria-invalid={invalidValue}
                                title={dup ? 'Duplicate value' : o.value === '' ? 'Value is required' : undefined}
                                value={o.value}
                                placeholder="value"
                                className="w-28 shrink-0 font-mono text-xs"
                                onChange={(e) => setValue(i, e.target.value)}
                            />
                            <Button type="button" variant="ghost" size="icon-xs" aria-label={`Move option ${i + 1} up`} disabled={i === 0} onClick={() => onChange(moveOption(options, i, -1))}>
                                <ArrowUp />
                            </Button>
                            <Button type="button" variant="ghost" size="icon-xs" aria-label={`Move option ${i + 1} down`} disabled={i === options.length - 1} onClick={() => onChange(moveOption(options, i, 1))}>
                                <ArrowDown />
                            </Button>
                            <Button type="button" variant="ghost" size="icon-xs" aria-label={`Remove option ${i + 1}`} onClick={() => onChange(removeOption(options, i))}>
                                <X />
                            </Button>
                        </li>
                    )
                })}
            </ul>
            <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={add}>
                    <Plus data-icon="inline-start" /> Add option
                </Button>
                <Popover open={bulkOpen} onOpenChange={setBulkOpen}>
                    <PopoverTrigger asChild>
                        <Button type="button" variant="outline" size="sm">
                            <ClipboardPaste data-icon="inline-start" /> Paste list
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72">
                        <FieldGroup className="gap-3">
                            <Field>
                                <FieldLabel htmlFor="bulk-options">One option per line</FieldLabel>
                                <Textarea id="bulk-options" rows={6} value={bulk} onChange={(e) => setBulk(e.target.value)} placeholder={'Low\nMedium\nHigh'} />
                            </Field>
                            <Button type="button" size="sm" disabled={!bulk.trim()} onClick={addBulk}>Add options</Button>
                        </FieldGroup>
                    </PopoverContent>
                </Popover>
            </div>
        </FieldSet>
    )
}
